import { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { useGetOrganisationPropertiesQuery } from '../gql/graphql.generated';
import { useAuthenticationService } from '../authentication/authentication-service';
import { useConfig } from '../backstage/BackstageProvider';

type TenantFeatureKey = 'clockIn' | 'pay';
type TenantFeatureOverride = boolean | null;

type TenantFeatureSettings = {
  backstage: Record<TenantFeatureKey, boolean>;
  overrides: Record<TenantFeatureKey, TenantFeatureOverride>;
  effective: Record<TenantFeatureKey, boolean>;
};

const FEATURE_PROPERTY_KEYS: Record<TenantFeatureKey, string> = {
  clockIn: 'features.clockInOut',
  pay: 'features.pay'
};

const defaultFeatureSettings: TenantFeatureSettings = {
  backstage: {
    clockIn: false,
    pay: false
  },
  overrides: {
    clockIn: null,
    pay: null
  },
  effective: {
    clockIn: false,
    pay: false
  }
};

const TenantFeatureSettingsContext = createContext<TenantFeatureSettings>(defaultFeatureSettings);

const parseFeatureOverride = (value: unknown): TenantFeatureOverride => {
  if (typeof value === 'boolean') {
    return value;
  }

  return null;
};

export const TenantFeatureSettingsProvider: FC<PropsWithChildren> = ({ children }) => {
  const tenantId = useAuthenticationService(state => state.getEffectiveTenantId());

  // Replaced the old manual suspend block with the new hook
  const config = useConfig();
  const backstageFlags = useMemo(
    () => ({
      clockIn: Boolean(config?.flags?.clockIn),
      pay: Boolean(config?.flags?.pay)
    }),
    [config]
  );

  const [{ data }] = useGetOrganisationPropertiesQuery({
    pause: !tenantId
  });

  const value = useMemo(() => {
    let properties: Record<string, unknown> = {};

    if (data?.getOrganisationProperties) {
      try {
        const parsed = JSON.parse(data.getOrganisationProperties);
        if (parsed && typeof parsed === 'object') {
          properties = parsed as Record<string, unknown>;
        }
      } catch {
        properties = {};
      }
    }

    const clockInOverride = parseFeatureOverride(properties[FEATURE_PROPERTY_KEYS.clockIn]);
    const payOverride = parseFeatureOverride(properties[FEATURE_PROPERTY_KEYS.pay]);

    return {
      backstage: backstageFlags,
      overrides: {
        clockIn: clockInOverride,
        pay: payOverride
      },
      effective: {
        clockIn: backstageFlags.clockIn && (clockInOverride ?? true),
        pay: backstageFlags.pay && (payOverride ?? true)
      }
    };
  }, [backstageFlags, data?.getOrganisationProperties]);

  return <TenantFeatureSettingsContext.Provider value={value}>{children}</TenantFeatureSettingsContext.Provider>;
};

export const useTenantFeatureEnabled = (feature: TenantFeatureKey) => {
  const context = useContext(TenantFeatureSettingsContext);
  return context.effective[feature];
};

export const useTenantFeatureSettings = () => {
  return useContext(TenantFeatureSettingsContext);
};
